import {
  isPortfolioCapitalOptimizationResponse,
  PORTFOLIO_OPTIMIZATION_EVIDENCE_SCHEMA_VERSION,
  PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY,
} from "./portfolioOptimizationDecisionEvidence";

const required = (value) => value !== undefined && value !== null && value !== "";
const normalize = (value) => typeof value === "string" ? value.toUpperCase() : value;
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const scenarioSet = (ids) => Array.isArray(ids) ? ids.map(String).sort() : null;

const authorityContext = (response) => ({
  scope: response?.scope,
  schemaVersion: response?.run_context?.schema_version,
  analyticsAuthority: response?.run_context?.analytics_authority,
  analysisLevel: response?.run_context?.analysis_level,
  scenarioIds: scenarioSet(response?.run_context?.scenario_ids),
  simulations: response?.run_context?.simulations,
  seed: response?.run_context?.seed,
  seedMode: normalize(response?.run_context?.seed_mode),
  currency: normalize(response?.run_context?.currency),
  economicHorizon: response?.run_context?.economic_horizon_years,
  methodologyEconomicHorizon: response?.methodology?.economic_horizon_years,
  objective: response?.methodology?.objective,
  exposureBasis: response?.run_context?.exposure_basis,
  correlationBasis: response?.methodology?.correlation,
  economicBasis: response?.methodology?.economic_basis,
  investmentCostBasis: response?.methodology?.investment_cost_basis,
});

const REQUIRED_CONTEXT_KEYS = [
  "scope", "schemaVersion", "analyticsAuthority", "analysisLevel", "scenarioIds",
  "simulations", "seedMode", "currency", "economicHorizon", "methodologyEconomicHorizon", "objective",
  "exposureBasis", "correlationBasis", "economicBasis", "investmentCostBasis",
];

export function compareManagementConstrainedOptimization(acceptedEvidence, constrainedResponse) {
  if (
    acceptedEvidence?.schemaVersion !== PORTFOLIO_OPTIMIZATION_EVIDENCE_SCHEMA_VERSION ||
    acceptedEvidence?.sourceAuthority !== PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY ||
    !isPortfolioCapitalOptimizationResponse(acceptedEvidence?.optimizerResponse) ||
    !isPortfolioCapitalOptimizationResponse(constrainedResponse)
  ) {
    return { status: "INCOMPARABLE", reason: "UNSUPPORTED_AUTHORITY_SCHEMA" };
  }

  const accepted = authorityContext(acceptedEvidence.optimizerResponse);
  const constrained = authorityContext(constrainedResponse);
  if (REQUIRED_CONTEXT_KEYS.some((key) => !required(accepted[key]) || !required(constrained[key]))) {
    return { status: "INCOMPARABLE", reason: "INCOMPLETE_AUTHORITY_CONTEXT" };
  }
  if (
    accepted.scope !== PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY ||
    constrained.scope !== PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY ||
    accepted.analyticsAuthority !== PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY ||
    constrained.analyticsAuthority !== PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY ||
    accepted.schemaVersion !== "1.0" || constrained.schemaVersion !== "1.0"
  ) {
    return { status: "INCOMPARABLE", reason: "UNSUPPORTED_AUTHORITY_SCHEMA" };
  }
  if (
    !same(accepted.economicHorizon, accepted.methodologyEconomicHorizon) ||
    !same(constrained.economicHorizon, constrained.methodologyEconomicHorizon)
  ) {
    return { status: "INCOMPARABLE", reason: "INCONSISTENT_ECONOMIC_HORIZON" };
  }

  const mismatch = REQUIRED_CONTEXT_KEYS.find((key) => !same(accepted[key], constrained[key]));
  if (mismatch) return { status: "INCOMPARABLE", reason: `${mismatch.toUpperCase()}_MISMATCH` };

  // A fixed seed must be identical. Exploratory runs are comparable only when both
  // authorities explicitly return the same (normally null) seed.
  if (!same(accepted.seed, constrained.seed)) {
    return { status: "INCOMPARABLE", reason: "SEED_MISMATCH" };
  }
  return { status: "COMPARABLE", reason: null };
}
